import { Injectable } from '@angular/core';
import { Observable, interval } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { ToastLoader } from '../toastLoader';

@Injectable({
  providedIn: 'root',
})
export class FunctionHelper {
  private secondsRemaining: number = 60;

  constructor(private toastLoader: ToastLoader) {}

  startTimer(
    totalSeconds: number = 60,
    showToast: boolean = false,
    toastIntervalSeconds: number = 10,
  ): Observable<number> {
    this.secondsRemaining = totalSeconds;

    return interval(1000).pipe(
      takeWhile(() => this.secondsRemaining > 0),

      map(() => {
        this.secondsRemaining--;

        if (
          showToast &&
          this.secondsRemaining > 0 &&
          this.secondsRemaining % toastIntervalSeconds === 0
        ) {
          this.toastLoader.show(
            200,
            `Faltam ${this.secondsRemaining} segundos!`,
          );
        }

        if (showToast && this.secondsRemaining == 0) {
          this.toastLoader.show(200, `Tempo esgotado!`);
        }

        return this.secondsRemaining;
      }),
    );
  }

  copyText(text: string) {
    navigator.clipboard.writeText(text);
    this.toastLoader.show(200, 'Texto copiado para área de transferência');
  }

  convertToFormData(
    data: Record<string, any>,
    formData: FormData = new FormData(),
    parentKey?: string,
  ): FormData {
    Object.entries(data).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      const fieldKey = parentKey ? `${parentKey}[${key}]` : key;

      if (value instanceof File || value instanceof Blob) {
        formData.append(fieldKey, value);
        return;
      }

      if (value instanceof Date) {
        const formatted =
          value.getFullYear() +
          '-' +
          String(value.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(value.getDate()).padStart(2, '0') +
          ' ' +
          String(value.getHours()).padStart(2, '0') +
          ':' +
          String(value.getMinutes()).padStart(2, '0') +
          ':' +
          String(value.getSeconds()).padStart(2, '0');

        formData.append(fieldKey, formatted);
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((element, index) => {
          if (
            typeof element !== 'object' ||
            element instanceof File ||
            element instanceof Blob ||
            element instanceof Date
          ) {
            if (element instanceof File || element instanceof Blob) {
              formData.append(`${fieldKey}[]`, element);
            } else if (element instanceof Date) {
              const formatted =
                element.getFullYear() +
                '-' +
                String(element.getMonth() + 1).padStart(2, '0') +
                '-' +
                String(element.getDate()).padStart(2, '0') +
                ' ' +
                String(element.getHours()).padStart(2, '0') +
                ':' +
                String(element.getMinutes()).padStart(2, '0') +
                ':' +
                String(element.getSeconds()).padStart(2, '0');

              formData.append(`${fieldKey}[]`, formatted);
            } else {
              formData.append(`${fieldKey}[]`, String(element));
            }
          } else {
            this.convertToFormData(element, formData, `${fieldKey}[${index}]`);
          }
        });

        return;
      }

      if (typeof value === 'object') {
        this.convertToFormData(value, formData, fieldKey);
        return;
      }

      formData.append(fieldKey, String(value));
    });

    return formData;
  }

  listRemoveByKey<T>(array: T[], key: keyof T, value: any): T[] {
    return array.filter((item) => item[key] !== value);
  }

  listAddByKey<T>(array: T[], key: keyof T, item: T): T[] {
    const index = array.findIndex((i) => i[key] === item[key]);

    if (index === -1) {
      return [...array, item];
    }

    const newArray = [...array];
    newArray[index] = item;

    return newArray;
  }

  listRemoveByKeyFromPaginated<T>(
    pagination: any,
    list: T[],
    key: keyof T,
    value: number | string,
  ): { pagination: any; list: T[] } {
    const newList = list.filter((item) => item[key] !== value);

    if (newList.length !== list.length) {
      pagination = {
        ...pagination,
        total: pagination.total - 1,
      };
    }

    return { pagination, list: newList };
  }

  listAddByKeyFromPaginated<T>(
    pagination: any,
    list: T[],
    key: keyof T,
    item: T,
  ): { pagination: any; list: T[] } {
    if (!item || !item[key]) {
      return { pagination, list };
    }

    const index = list.findIndex((i) => i[key] === item[key]);

    if (index !== -1) {
      const newList = [...list];
      newList[index] = item;
      return { pagination, list: newList };
    }

    return {
      pagination: {
        ...pagination,
        total: pagination.total + 1,
      },
      list: [item, ...list],
    };
  }

  listMerge<T>(existing: any[], incoming: any[], key: keyof T) {
    const existingMap = new Map(existing.map((item, i) => [item[key], i]));

    for (const newItem of incoming) {
      const index = existingMap.get(newItem[key]);

      if (index !== undefined) {
        existing[index] = newItem;
      } else {
        existing.push(newItem);
      }
    }

    return existing;
  }

  async processImage(file: File) {
    const fileName = file.name.toLowerCase();

    const validMimeTypes = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/heic',
      'image/heif',
    ];

    const isHeic = fileName.endsWith('.heic') || fileName.endsWith('.heif');

    const isValidType =
      (file.type && validMimeTypes.includes(file.type)) || isHeic;

    if (!isValidType) {
      this.toastLoader.show(200, 'Só é permitido selecionar imagens');
      return;
    }

    let processedFile = file;

    if (isHeic) {
      try {
        const { default: heic2any } = await import('heic2any');
        const convertedBlob: any = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.9,
        });

        processedFile = new File(
          [convertedBlob],
          file.name.replace(/\.(heic|heif)/i, '.jpg'),
          { type: 'image/jpeg' },
        );
      } catch (error) {
        this.toastLoader.show(200, 'Erro ao converter imagem HEIC');
        return;
      }
    }

    try {
      processedFile = await this.compressAndResizeImage(processedFile);
    } catch (error) {
      this.toastLoader.show(200, 'Erro ao processar imagem');
      return;
    }

    return processedFile;
  }

  async compressAndResizeImage(
    file: File,
    maxWidth = 1280,
    quality = 0.8,
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        ctx!.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject('Erro ao comprimir imagem');

            const newFile = new File(
              [blob],
              file.name.replace(/\.\w+$/, '.jpg'),
              { type: 'image/jpeg' },
            );

            resolve(newFile);
          },
          'image/jpeg',
          quality, 
        );
      };

      img.onerror = reject;

      img.src = URL.createObjectURL(file);
    });
  }

  async withMinimumDelay<T>(action: Promise<T>, delayMs: number): Promise<T> {
    const [result] = await Promise.all([
      action,
      new Promise((resolve) => setTimeout(resolve, delayMs)),
    ]);

    return result;
  }
}
